import React, { useState, useMemo } from 'react'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import { PlayIcon, VideoCameraIcon } from '@heroicons/react/24/solid'
import { LocalTimeBadge } from '../../../shared/components/LocalTimeBadge'
import { INTERAX_COLORS } from '../../../constants/colors'
import { TYPOGRAPHY } from '../../../constants/interaxColors'
import {
  GLASSY_BUTTON_ICON_STYLE,
  GlassyActionButton,
  ButtonLink,
  LiveBadge,
  RecommendationsContainer
} from '../../../interaxDesignSystemComponents'
import { SectionHeader, EmptyStateCard } from './cards'
import { CalendarIcon } from '@heroicons/react/24/outline'
import { useTeam } from '../../../apiLayer/queryHooks/queries'
import { CountdownTimer } from './CountdownTimer'
import { ActivityWithSession } from '../../../types/GeneralTypes'
import { isAIDebriefActivity, isGameActivityType } from '../../../../../pure-js/libs/isAIDebriefActivity'
import { activityUsesSprintRoom } from '../../../../../pure-js/libs/activityUsesDedicatedRoom'
import { buildSprintCallPath } from '../../../config/routes/routeConfig'
import { hasPlayedEveryConfiguredLoop } from '../helpers/gameSessionLatch'
import { isAIDebriefOpenForTeam } from '../../shared/helpers/liveActivityHelpers'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { TESTIDS } from '../../../config/testids'

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc)
dayjs.extend(timezone)

type GamerActivity = ActivityWithSession

interface NextActivityGamerComponentProps {
  activity?: GamerActivity | null
  isFromFallback?: boolean
  userTeamIds: string[]
  onViewAll?: () => void
  onEnterLobby: () => void
  className?: string
}

type ButtonState = 'empty' | 'fallback' | 'countdown' | 'ready'

/** How long past its scheduled end a game nobody has ended can still be joined. Generous, since sprints
 * overrun; what actually closes an activity is `endedAt`. A debrief has no cutoff here: the backend
 * only serves an overdue one while the team is still in it, up to its own 3 h backstop. */
const LATE_JOIN_GRACE_MINUTES = 120 // 2 hours

/**
 * NextActivityGamerComponent - Displays the next upcoming activity for gamer
 * Shows activity date, type, sprint, team, live status, and action button
 */
export const NextActivityGamerComponent: React.FC<NextActivityGamerComponentProps> = ({
  activity,
  isFromFallback = false,
  userTeamIds,
  onViewAll,
  onEnterLobby,
  className = ''
}) => {
  const { t } = useTranslation()
  const [forceReady, setForceReady] = useState(false)

  // Find user's team that matches activity teams
  const userTeamId = activity ? userTeamIds.find((id) => activity.teamIds.includes(id)) : undefined
  const { data: teamResponse } = useTeam(userTeamId || '', { enabled: !!userTeamId })
  const teamName = teamResponse?.data?.name || 'Team'

  const hasPlayedEveryLoop = hasPlayedEveryConfiguredLoop({
    numberOfLoops: activity?.numberOfLoops,
    isLoopedGame: isGameActivityType(activity?.type)
  })

  // Determine button state based on activity data and timing
  const buttonState: ButtonState = useMemo(() => {
    // Case 1: No activity
    if (!activity) return 'empty'

    // Case 2: Fallback activity (no active session)
    if (isFromFallback) return 'fallback'

    // Case 3: Activity has ended. `endedAt` is what actually ends one — a scheduled window that has
    // passed does not, or a session running late locks its own participants out of joining it.
    if (activity.endedAt) return 'fallback'
    // Every configured loop has been played. The backend reports 0 rather than flooring at 1, because
    // the game reads 0 as "no fixed count" — so the launch has to be refused here instead.
    if (hasPlayedEveryLoop) return 'fallback'
    const isGamePastLateJoinGrace =
      isGameActivityType(activity.type) &&
      Boolean(activity.endDate) &&
      dayjs().isAfter(dayjs(activity.endDate).add(LATE_JOIN_GRACE_MINUTES, 'minute'))
    if (isGamePastLateJoinGrace) return 'fallback'
    if (isAIDebriefOpenForTeam(activity)) return 'ready'

    // Case 5: Determine start time - use session createdAt or activity startDate
    let startTimeStr: string | null = null

    if (activity.session?.createdAt) {
      startTimeStr = activity.session.createdAt
    } else if (activity.startDate) {
      startTimeStr = activity.startDate
    } else {
      return 'fallback'
    }

    // Force ready state when countdown reaches 0
    if (forceReady) {
      return 'ready'
    }

    // Use dayjs for better timezone handling
    const now = dayjs()
    const startTime = dayjs(startTimeStr)
    const fiveMinsBefore = startTime.subtract(5, 'minutes')

    // Case 6: More than 5 mins before → countdown
    if (now.isBefore(fiveMinsBefore)) {
      return 'countdown'
    }

    // Case 7: Within 5 mins or started → show button

    return 'ready'
  }, [activity, isFromFallback, forceReady, hasPlayedEveryLoop])

  // Calculate live status
  const isLive = useMemo(() => {
    if (!activity) return false
    if (activity.endedAt) return false
    if (activity.endDate && dayjs().isAfter(dayjs(activity.endDate))) return false

    // If the user's team has already completed this activity, it's not live for them
    if (userTeamIds.length && activity.completedActivityTeamIds?.some((id) => userTeamIds.includes(id))) {
      return false
    }
    if (isAIDebriefOpenForTeam(activity)) return true

    const startTimeStr = activity.session?.createdAt ?? activity.startDate ?? null
    if (!startTimeStr) return false

    const now = dayjs()
    const startTime = dayjs(startTimeStr)
    const fiveMinsBefore = startTime.subtract(5, 'minutes')

    return now.isAfter(fiveMinsBefore) || now.isSame(fiveMinsBefore)
  }, [activity, userTeamIds])

  // Format date parts
  const formatDateParts = () => {
    if (!activity) {
      return { day: '--', month: 'N/A' }
    }

    // Use session date if available, otherwise fall back to activity startDate
    let dateString: string | undefined
    if (activity.session?.createdAt) {
      dateString = activity.session.createdAt
    } else if (activity.startDate) {
      dateString = activity.startDate
    }

    if (!dateString) {
      return { day: '--', month: 'N/A' }
    }

    const date = dayjs(dateString)
    const day = date.format('DD')
    const month = date.format('MMM').toUpperCase()

    return { day, month }
  }

  const { day, month } = formatDateParts()

  // Get translated activity type
  const getActivityTypeTranslation = (type?: string): string => {
    if (!type) return t('gamer.activityTypes.default', 'Activity')
    return t(`gamer.activityTypes.${type}`, type)
  }

  const isGameActivity = isGameActivityType(activity?.type)

  const countdownLabel = isGameActivity
    ? t('gamer.nextActivity.countdownLabel', 'until game starts')
    : t('gamer.nextActivity.activityCountdownLabel', 'until activity starts')

  // The AI coached team debrief has a lobby of its own; the sprint's calls are joined with the button below.
  const isReflectionActivity = isAIDebriefActivity(activity?.type)

  const navigate = useNavigate()

  const handleEnterReflectionLobby = () => {
    navigate(GamerRoutes.AI_COACH)
  }

  // The introduction and the group reflection are one call for the whole sprint, on a page of its own.
  const canJoinSprintCall = buttonState === 'ready' && activityUsesSprintRoom(activity?.type)

  const handleJoinSprintCall = () => {
    if (activity) navigate(buildSprintCallPath({ sprintId: activity.sprintId, activityId: activity.id }))
  }

  return (
    <RecommendationsContainer
      className={className}
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <SectionHeader
        title={t('gamer.nextActivity.title', 'Next activity')}
        subtitle={t('gamer.nextActivity.subtitle', 'Upcoming activities will show here')}
        action={
          activity && onViewAll ? (
            <ButtonLink onClick={onViewAll}>{t('gamer.nextActivity.viewAll', 'View all ->')}</ButtonLink>
          ) : undefined
        }
      />

      {/* Activity Content or Empty State */}
      {activity ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Activity Card */}
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              border: `1px solid ${INTERAX_COLORS.thunderGrey20}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5
            }}
          >
            {/* Activity Info Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* Left: Date and Activity Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Date */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: '50px'
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '28px',
                      fontWeight: 600,
                      color: INTERAX_COLORS.sun,
                      lineHeight: 1
                    }}
                  >
                    {day}
                  </Box>
                  <Box
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: INTERAX_COLORS.thunderGrey,
                      lineHeight: 1
                    }}
                  >
                    {month}
                  </Box>
                </Box>

                {/* Activity Details */}
                <Box>
                  <Box
                    sx={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: INTERAX_COLORS.white,
                      mb: 0.5
                    }}
                  >
                    {getActivityTypeTranslation(activity.type)}
                  </Box>
                  <Box
                    sx={{
                      fontSize: '14px',
                      color: INTERAX_COLORS.thunderGrey
                    }}
                  >
                    {activity.sprintTitle} • {teamName}
                  </Box>
                </Box>
              </Box>

              {/* Right: Live Status - uses shared LiveBadge with pulse animation */}
              {isLive && <LiveBadge size="medium" />}
            </Box>
          </Box>

          {/* Action Area - Conditional based on button state AND activity type */}
          {buttonState === 'ready' &&
            isGameActivity &&
            (() => {
              return (
                <GlassyActionButton
                  variant="orange"
                  icon={<PlayIcon style={GLASSY_BUTTON_ICON_STYLE} />}
                  onClick={onEnterLobby}
                  fullWidth
                  data-testid={TESTIDS.UPCOMING_ENTER_LOBBY_BTN}
                >
                  {t('gamer.buttons.enterTeamLobby', 'Enter team lobby')}
                </GlassyActionButton>
              )
            })()}

          {buttonState === 'ready' &&
            isReflectionActivity &&
            (() => {
              return (
                <GlassyActionButton
                  variant="orange"
                  icon={<PlayIcon style={GLASSY_BUTTON_ICON_STYLE} />}
                  onClick={handleEnterReflectionLobby}
                  fullWidth
                  data-testid={TESTIDS.UPCOMING_ENTER_REFLECTION_LOBBY_BTN}
                >
                  {t('gamer.buttons.enterReflectionLobby', 'Enter reflection lobby')}
                </GlassyActionButton>
              )
            })()}

          {canJoinSprintCall && (
            <GlassyActionButton
              variant="orange"
              icon={<VideoCameraIcon style={GLASSY_BUTTON_ICON_STYLE} />}
              onClick={handleJoinSprintCall}
              fullWidth
              data-testid={TESTIDS.UPCOMING_JOIN_SPRINT_CALL_BTN}
            >
              {t('gamer.upcomingActivities.joinNowButton', 'Join Now')}
            </GlassyActionButton>
          )}

          {buttonState === 'countdown' &&
            activity &&
            (() => {
              // Determine start time for countdown
              const startTimeStr = activity.session?.createdAt ?? activity.startDate ?? null

              if (!startTimeStr) return null

              const startTime = dayjs(startTimeStr)
              const targetTime = startTime.subtract(5, 'minutes')

              return (
                <CountdownTimer
                  targetTime={targetTime.toDate()}
                  label={countdownLabel}
                  onTimeReached={() => setForceReady(true)}
                />
              )
            })()}

          {buttonState === 'fallback' &&
            activity &&
            (() => {
              // Determine start time for fallback message
              const startTimeStr = activity.session?.createdAt ?? activity.startDate ?? null

              if (!startTimeStr) return null

              const startTime = dayjs(startTimeStr)
              const now = dayjs()
              const endTime = activity.endDate ? dayjs(activity.endDate) : null
              /** After scheduled end: show past-tense copy instead of "Next game scheduled for". */
              const scheduledWindowEnded = Boolean(endTime && now.isAfter(endTime))
              const isToday = startTime.isSame(now, 'day')

              return (
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: INTERAX_COLORS.heritageBlue40,
                    borderRadius: '12px',
                    border: `1px solid ${INTERAX_COLORS.thunderGrey20}`
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {scheduledWindowEnded ? (
                      <>
                        <Box sx={{ ...TYPOGRAPHY.body1, color: INTERAX_COLORS.thunderGrey }}>
                          {t(
                            'gamer.nextActivity.scheduledWindowEnded',
                            "This activity's scheduled time slot has ended."
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Box component="span" sx={{ ...TYPOGRAPHY.body1, color: INTERAX_COLORS.thunderGrey }}>
                            {t('gamer.nextActivity.sessionWasScheduledFor', 'It was scheduled to start at')}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              ...TYPOGRAPHY.body1,
                              color: INTERAX_COLORS.sun,
                              fontWeight: 600
                            }}
                          >
                            {startTime.format('MMM D, h:mm A')}
                          </Box>
                          <LocalTimeBadge />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={{ ...TYPOGRAPHY.body1, color: INTERAX_COLORS.thunderGrey }}>
                          {isToday
                            ? t('gamer.nextActivity.noScheduledGameToday', 'There is no scheduled game for today.')
                            : t('gamer.nextActivity.noScheduledGame', 'There is no currently scheduled game.')}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Box component="span" sx={{ ...TYPOGRAPHY.body1, color: INTERAX_COLORS.thunderGrey }}>
                            {t('gamer.nextActivity.nextGameScheduledFor', 'Next game scheduled for')}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              ...TYPOGRAPHY.body1,
                              color: INTERAX_COLORS.sun,
                              fontWeight: 600
                            }}
                          >
                            {startTime.format('MMM D, h:mm A')}
                          </Box>
                          <LocalTimeBadge />
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              )
            })()}
        </Box>
      ) : (
        <EmptyStateCard
          icon={
            <CalendarIcon
              style={{
                width: '32px',
                height: '32px',
                color: INTERAX_COLORS.sun
              }}
            />
          }
          message={t('gamer.nextActivity.noActivity', 'No upcoming activities')}
        />
      )}
    </RecommendationsContainer>
  )
}
